"use client"

import { useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { parseSRT } from "./SRTParser"
import TemplateSelector from "./TemplateSelector"
import ModernTemplateSelector from "./ModernTemplateSelector"
import { parseContent } from "./utils/text-parser"

/**
 * Enhanced SuperSimpleSynchronizer component
 *
 * This component synchronizes the displayed content with the avatar's speech timing
 * by using the subtitle number/index in the SRT file to determine which content part to show.
 * It supports both the legacy format (array of parts) and the new format (single content with highlight metadata).
 */
const SuperSimpleSynchronizer = ({
  content,
  audioTime,
  isPlaying,
  srtSrc,
  currentLine,
  type = "content",
  onSelectAnswer,
  selectedAnswer,
  isChecking,
  ...props
}) => {
  const [contentPartIndex, setContentPartIndex] = useState(0)
  const [subtitles, setSubtitles] = useState([])
  const [imageVisible, setImageVisible] = useState(true)
  const [containerHeight, setContainerHeight] = useState(null)
  const [lastContent, setLastContent] = useState(null)
  const [processedContent, setProcessedContent] = useState(null)
  const contentRef = useRef(null)
  const contentKey = useRef(0)
  const resizeObserverRef = useRef(null)

  // Helper to detect if we're using the legacy format (array of parts)
  const isLegacyFormat = () => {
    return content?.parts && Array.isArray(content.parts)
  }

  // Helper to safely parse SRT files
  const safeParseSRT = (content) => {
    if (!content || typeof content !== "string") {
      console.warn("Invalid SRT content")
      return []
    }

    // Check if content looks like HTML instead of SRT
    if (content.trim().startsWith("<!DOCTYPE") || content.trim().startsWith("<html")) {
      console.warn("Received HTML instead of SRT content")
      return []
    }

    try {
      // Basic SRT format validation before parsing
      if (!content.includes(" --> ")) {
        console.warn("Invalid SRT format: missing timestamp separator")
        return []
      }

      return parseSRT(content)
    } catch (error) {
      console.warn("Error parsing SRT content:", error)
      return []
    }
  }

  // Parse the SRT file to get subtitles
  useEffect(() => {
    const loadSubtitles = async () => {
      // If no SRT file exists, show the first content part only
      if (!srtSrc) {
        setSubtitles([])
        return
      }

      try {
        const response = await fetch(srtSrc)
        const srtContent = await response.text()
        const parsedSubtitles = safeParseSRT(srtContent)
        setSubtitles(parsedSubtitles)
      } catch (error) {
        console.error("Error loading subtitles:", error)
        setSubtitles([])
      }
    }

    loadSubtitles()
  }, [srtSrc])

  // Track when actual content changes to update the content key
  useEffect(() => {
    // Check if the main content object has changed structurally
    if (content && (!lastContent || content.title !== lastContent.title)) {
      // Content has changed significantly, increment key to force re-render
      contentKey.current += 1
      setLastContent(content)
    }
  }, [content, lastContent])

  // LEGACY FORMAT HANDLING: Update content part based on current line
  useEffect(() => {
    if (!isLegacyFormat()) return

    if (content?.parts && Array.isArray(content.parts)) {
      const maxIndex = content.parts.length - 1
      // Map subtitle line numbers to content parts
      const newIndex = Math.min(currentLine, maxIndex)

      if (newIndex !== contentPartIndex) {
        setContentPartIndex(newIndex)
        // Flash the image when changing parts to draw attention
        setImageVisible(false)
        setTimeout(() => setImageVisible(true), 150)
      }
    }
  }, [currentLine, content, contentPartIndex])

  // LEGACY FORMAT HANDLING: Update content part based on current audio time and subtitle index
  useEffect(() => {
    if (!isLegacyFormat() || !isPlaying || subtitles.length === 0) return

    for (let i = 0; i < subtitles.length; i++) {
      const subtitle = subtitles[i]
      if (audioTime >= subtitle.start && audioTime <= subtitle.end) {
        // Use the subtitle index directly (or limit to max parts available)
        const maxIndex = content?.parts ? content.parts.length - 1 : 0
        const newIndex = Math.min(i, maxIndex)

        if (newIndex !== contentPartIndex) {
          setContentPartIndex(newIndex)
          // Flash the image when changing parts to draw attention
          setImageVisible(false)
          setTimeout(() => setImageVisible(true), 150)
        }
        break
      }
    }
  }, [audioTime, isPlaying, subtitles, contentPartIndex, content])

  // NEW FORMAT HANDLING: Process content based on current line (from SRT)
  useEffect(() => {
    // Skip processing for legacy format
    if (isLegacyFormat() || !content) {
      return
    }

    // For new format, process the content based on current line
    try {
      // Create a deep copy of the content to modify
      const newContent = JSON.parse(JSON.stringify(content))

      // IMPORTANT: Explicitly preserve timer properties
      newContent.showTimer = content.showTimer
      newContent.timerMinutes = content.timerMinutes
      newContent.timerSeconds = content.timerSeconds

      // Process columns if they exist
      if (newContent.columns && Array.isArray(newContent.columns)) {
        newContent.columns = newContent.columns.map((column) => {
          // Process image changes based on SRT index
          if (column.imageChanges && Array.isArray(column.imageChanges)) {
            const matchingChange = column.imageChanges.find((change) => change.srtIndex === currentLine + 1)

            if (matchingChange) {
              column.imageSrc = matchingChange.src
              // Flash the image when changing
              setImageVisible(false)
              setTimeout(() => setImageVisible(true), 150)
            }
          }

          // Process list items with highlight attributes
          if (column.listItems && Array.isArray(column.listItems)) {
            // Create a new array for processed list items
            const processedListItems = []
            let highlightIndex = -1

            // Process each list item
            column.listItems.forEach((item, index) => {
              // If it's already a string, add it as is
              if (typeof item === "string") {
                processedListItems.push(item)
                return
              }

              // If it's an object with text property, extract the text
              if (typeof item === "object" && item.text) {
                processedListItems.push(item.text)

                // Check if this item should be highlighted
                if (item.highlightAtSrtIndex === currentLine + 1) {
                  highlightIndex = index
                }

                // Check if this item should be shown/hidden
                if (item.showAtSrtIndex !== undefined && item.showAtSrtIndex > currentLine + 1) {
                  // Don't show this item yet
                  processedListItems.pop()
                }
              }
            })

            // Set the processed list items
            column.listItems = processedListItems

            // Set highlight properties if an item should be highlighted
            if (highlightIndex !== -1) {
              column.highlight = true
              column.highlightItem = highlightIndex
            } else {
              column.highlight = false
              delete column.highlightItem
            }
          }

          return column
        })
      }

      // Process tips if they exist
      if (newContent.tips && Array.isArray(newContent.tips)) {
        newContent.tips = newContent.tips
          .map((tip) => {
            // Create a new tip object with just the text and icon
            const newTip = {
              text: typeof tip === "string" ? tip : tip.text,
              icon: tip.icon,
            }

            // Check if this tip should be highlighted based on SRT index
            if (tip.highlightAtSrtIndex === currentLine + 1) {
              newTip.highlight = true
            } else {
              newTip.highlight = false
            }

            // Check if this tip should be shown/hidden
            if (tip.showAtSrtIndex !== undefined && tip.showAtSrtIndex > currentLine + 1) {
              // Don't show this tip yet - we'll filter it out
              return null
            }

            return newTip
          })
          .filter((tip) => tip !== null) // Remove any null tips (hidden ones)
      }

      // Process tools if they exist
      if (newContent.tools && Array.isArray(newContent.tools)) {
        newContent.tools = newContent.tools.map((tool) => {
          // Process observations with highlight attributes
          if (tool.observations && Array.isArray(tool.observations)) {
            // Create a new array for processed observations
            const processedObservations = []
            let highlightIndex = -1

            // Process each observation
            tool.observations.forEach((observation, index) => {
              // If it's already a string, add it as is
              if (typeof observation === "string") {
                processedObservations.push(observation)
                return
              }

              // If it's an object with text property, extract the text
              if (typeof observation === "object" && observation.text) {
                processedObservations.push(observation.text)

                // Check if this observation should be highlighted
                if (observation.highlightAtSrtIndex === currentLine + 1) {
                  highlightIndex = index
                }

                // Check if this observation should be shown/hidden
                if (observation.showAtSrtIndex !== undefined && observation.showAtSrtIndex > currentLine + 1) {
                  // Don't show this observation yet
                  processedObservations.pop()
                }
              }
            })

            // Set the processed observations
            tool.observations = processedObservations

            // Set highlight properties if an observation should be highlighted
            if (highlightIndex !== -1) {
              tool.highlight = true
              tool.highlightItem = highlightIndex
            } else {
              tool.highlight = false
              delete tool.highlightItem
            }
          }

          return tool
        })
      }

      setProcessedContent(newContent)
    } catch (error) {
      console.error("Error processing content:", error)
      // Fallback to original content
      setProcessedContent(content)
    }
  }, [content, currentLine])

  // Handle image animation independently of content transitions
  useEffect(() => {
    if (isLegacyFormat() && content?.parts?.[contentPartIndex]?.imageSrc && !imageVisible) {
      const timer = setTimeout(() => setImageVisible(true), 150)
      return () => clearTimeout(timer)
    }
  }, [contentPartIndex, imageVisible, content])

  // Find the maximum content height after initial render to fix bouncing
  useEffect(() => {
    // Initialize an array to track heights if needed
    const contentHeights = []

    // Measure initial content height
    if (contentRef.current && !containerHeight) {
      const height = contentRef.current.offsetHeight
      contentHeights.push(height)

      // Set initial container height (with a minimum height)
      const minHeight = Math.max(400, height)
      setContainerHeight(minHeight)
    }

    // Set up resize observer to handle content changes
    if (contentRef.current && !resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // If content height changes significantly, update highlighted element scroll
          if (entry.target === contentRef.current) {
            // Find any highlighted elements and scroll them into view
            const highlightedElements = entry.target.querySelectorAll('[data-highlighted="true"]')
            if (highlightedElements.length > 0) {
              setTimeout(() => {
                scrollToHighlightedElement(highlightedElements[0])
              }, 200)
            }
          }
        }
      })

      resizeObserverRef.current.observe(contentRef.current)
    }

    // Cleanup
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
    }
  }, [containerHeight])

  // Helper function to scroll highlighted elements into view
  const scrollToHighlightedElement = (element) => {
    if (!element) return

    const rect = element.getBoundingClientRect()
    const isMobile = window.innerWidth <= 768

    // Calculate the bottom safe area to keep content above the avatar
    const avatarHeight = isMobile ? 220 : 150
    const bottomSafeArea = window.innerHeight - avatarHeight

    // Check if element is already visible in the safe viewport area
    const isFullyVisible =
      rect.top >= 100 && // Allow some space at the top
      rect.bottom <= bottomSafeArea // Keep above the avatar

    if (!isFullyVisible) {
      // For mobile, position the element higher in the viewport
      if (isMobile) {
        // Calculate position that places element in the upper portion of screen
        const yOffset = -Math.max(window.innerHeight * 0.3, 200) // 40% from top or minimum 200px
        const y = rect.top + window.pageYOffset + yOffset

        window.scrollTo({
          top: Math.max(0, y),
          behavior: "smooth",
        })
      } else {
        // Desktop behavior - center in viewport with margin
        const yOffset = -150
        const y = rect.top + window.pageYOffset + yOffset

        window.scrollTo({
          top: Math.max(0, y),
          behavior: "smooth",
        })
      }
    }
  }

  // Scroll to top when content changes significantly
  useEffect(() => {
    // When content changes significantly (based on contentKey), scroll to top
    window.scrollTo({
      top: 0,
      behavior: "auto",
    })

    // After a small delay, ensure any highlighted content is visible
    const scrollTimeout = setTimeout(() => {
      if (contentRef.current) {
        const highlightedElements = contentRef.current.querySelectorAll('[data-highlighted="true"]')
        if (highlightedElements.length > 0) {
          scrollToHighlightedElement(highlightedElements[0])
        }
      }
    }, 300)

    return () => clearTimeout(scrollTimeout)
  }, [contentKey.current])

  // Monitor current line changes to scroll
  useEffect(() => {
    // Small delay to allow highlighted elements to update
    const scrollTimeout = setTimeout(() => {
      if (contentRef.current) {
        const highlightedElements = contentRef.current.querySelectorAll('[data-highlighted="true"]')
        if (highlightedElements.length > 0) {
          scrollToHighlightedElement(highlightedElements[0])
        }
      }
    }, 200)

    return () => clearTimeout(scrollTimeout)
  }, [currentLine])

  // LEGACY FORMAT: Determine which content parts to show
  const getVisibleContent = () => {
    // If we're using the new format, return the processed content
    if (!isLegacyFormat()) {
      return processedContent || content
    }

    // Legacy format handling
    if (content?.parts && Array.isArray(content.parts)) {
      // If the current index is valid, return that part's content
      if (contentPartIndex >= 0 && contentPartIndex < content.parts.length) {
        return {
          ...content,
          ...content.parts[contentPartIndex],
          imageVisible: imageVisible,
          currentPartIndex: contentPartIndex,
          totalParts: content.parts.length,
        }
      }
    }

    // Default: return the full content with additional properties
    return {
      ...content,
      imageVisible: imageVisible,
      currentPartIndex: 0,
      totalParts: 1,
    }
  }

  // Get the content to display based on format
  const currentContent = isLegacyFormat() ? getVisibleContent() : processedContent || content

  // Component for synchronized image display
  const SynchronizedImage = ({ src, alt, visible }) => {
    if (!src) return null

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{
          opacity: visible ? 1 : 0,
          scale: visible ? 1 : 0.95,
        }}
        transition={{ duration: 0.3 }}
        className="mt-4 mb-6 flex justify-center"
        style={{ minHeight: src ? "150px" : "0" }} // Maintain minimum height for image area
      >
        <img
          src={src || "/placeholder.svg"}
          alt={alt || "Illustration"}
          className="max-w-full rounded-lg shadow-sm max-h-60 object-contain"
        />
      </motion.div>
    )
  }

  // Helper function to process content before passing to TemplateSelector
  const prepareContentForRendering = (content) => {
    if (!content) return content

    // Create a deep copy to avoid modifying the original
    const preparedContent = JSON.parse(JSON.stringify(content))

    // Process listItems to ensure they're strings, not objects
    const processListItems = (items) => {
      if (!Array.isArray(items)) return items

      return items.map((item) => {
        if (typeof item === "string") return item
        if (item && typeof item === "object" && "text" in item) return item.text
        return String(item) // Fallback
      })
    }

    // Process columns
    if (preparedContent.columns && Array.isArray(preparedContent.columns)) {
      preparedContent.columns = preparedContent.columns.map((column) => {
        if (column.listItems) {
          column.listItems = processListItems(column.listItems)
        }
        if (column.description) {
          column.description = parseContent(column.description)
        }
        if (typeof column.content === "string") {
          column.content = parseContent(column.content)
        }
        return column
      })
    }

    // Process tools
    if (preparedContent.tools && Array.isArray(preparedContent.tools)) {
      preparedContent.tools = preparedContent.tools.map((tool) => {
        if (tool.observations) {
          tool.observations = processListItems(tool.observations)
        }
        return tool
      })
    }

    // Process tips
    if (preparedContent.tips && Array.isArray(preparedContent.tips)) {
      preparedContent.tips = preparedContent.tips.map((tip) => {
        if (typeof tip === "string") return tip
        if (tip && typeof tip === "object" && "text" in tip) {
          return { ...tip, text: tip.text }
        }
        return tip
      })
    }

    return preparedContent
  }

  // Prepare the content for rendering
  const renderReadyContent = prepareContentForRendering(currentContent)

  // NEW CODE: Determine which template selector to use
  const useModernTemplate = type === "modern" || renderReadyContent?.useModernTemplate === true

  return (
    <div className="relative">
      {/* Display synchronized image if present */}
      <SynchronizedImage src={renderReadyContent?.imageSrc} alt={renderReadyContent?.imageAlt} visible={imageVisible} />

      {/* Main content container that remains stable */}
      <div ref={contentRef} key={`content-container-${contentKey.current}`} className="w-full">
        {/* Choose between modern and classic template selector */}
        {useModernTemplate ? (
          <ModernTemplateSelector
            type={type}
            content={renderReadyContent}
            onSelectAnswer={onSelectAnswer}
            selectedAnswer={selectedAnswer}
            isChecking={isChecking}
            currentLine={currentLine}
            fullscreenMode={renderReadyContent?.fullscreenContent}
            {...props}
          />
        ) : (
          <TemplateSelector
            type={type}
            content={renderReadyContent}
            onSelectAnswer={onSelectAnswer}
            selectedAnswer={selectedAnswer}
            isChecking={isChecking}
            currentLine={currentLine}
            fullscreenMode={renderReadyContent?.fullscreenContent}
            {...props}
          />
        )}
      </div>
    </div>
  )
}

export default SuperSimpleSynchronizer
